import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PublicLayoutProps {
  children: React.ReactNode;
}

// Version from build-time variable
declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

// Check if we're in staging environment
const isStaging = import.meta.env.VITE_APP_ENV === 'staging';

export function PublicLayout({ children }: PublicLayoutProps) {
  const { t } = useTranslation(['common', 'auth']);
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Staging Banner - sticky at top */}
      {isStaging && (
        <div className="sticky top-0 z-50 text-center text-xs py-1 font-medium" style={{ backgroundColor: '#DDD19C', color: '#5C5630' }}>
          {t('common:staging.banner')}
        </div>
      )}

      {/* Header */}
      <header className={`sticky ${isStaging ? 'top-6' : 'top-0'} z-40 bg-white shadow-sm ${isStaging ? 'border-t-2' : ''}`} style={isStaging ? { borderTopColor: '#DDD19C' } : {}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/login" className="flex items-center">
              <img src="/logo.svg" alt="Dein Marketing Hub" className="h-9 w-9 rounded-lg mr-2" />
              <div className="flex flex-col leading-none">
                <span className="text-sm font-medium text-gray-500">Dein</span>
                <span className="text-sm font-bold text-gray-900">MarketingHub</span>
              </div>
            </Link>

            {/* Login Button */}
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('auth:login')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="sticky bottom-0 py-3 text-center text-xs text-gray-400 bg-gray-50 border-t border-gray-200">
        <span>Dein Marketing Hub v{APP_VERSION}</span>
        <span className="mx-2">·</span>
        <Link to="/impressum" className="hover:text-gray-600">{t('common:footer.imprint')}</Link>
        <span className="mx-2">·</span>
        <Link to="/datenschutz" className="hover:text-gray-600">{t('common:footer.privacy')}</Link>
      </footer>
    </div>
  );
}
